import { Typography } from "@material-ui/core";
import FormControl from "@material-ui/core/FormControl";
import { PlaceSelectArrayInput } from "components/inputs/PlaceSelectArrayInput";
import { YesNoINoneInput } from "components/inputs/YesNoINoneInput";
import { NotCustomerRoles } from "entities/UserRole";
import React from "react";
import {
	BooleanInput,
	TextInput,
	maxLength,
	required,
	usePermissions,
	useTranslate,
} from "react-admin";
import { FormSpy } from "react-final-form";
import { Field } from "react-final-form";
import { isInArray } from "utils/general";

import { useEnumSelectChoices } from "../../components/_helpers/useEnumSelectChoices";
import { DocumentsFieldBlock } from "../../components/fields/DocumentsField";
import { FormGroupGrid, GridForm, GridInput } from "../../components/GridForm";
import { CompanyText, DateTimeInput, NumberInputEx } from "../../components/inputs";
import { ActionToggleButtonGroupInput } from "../../components/inputs/ActionToggleButtonGroupInput";
import { OSFCUserSelectInput, OSFCUsersSelectInput } from "../../components/inputs/OSFCUserSelect";
import { SelectEnumInput } from "../../components/inputs/SelectEnumInput";
import { DeviationDocumentType } from "../../entities/DeviationDocumentType";
import { DeviationMainCategory } from "../../entities/DeviationMainCategory";
import {
	CustomerAction,
	DeviationStatus,
	EmployeeAction,
	FirstManagerAction,
	ManagementAction,
	SecondManagerAction,
} from "../../entities/DeviationStatus";
import {
	DeviationTypeByMainCategory,
	IsImageRequiredByDeviationType,
} from "../../entities/DeviationType";
import { OrderStatus } from "../../entities/OrderStatus";
import { UserType } from "../../entities/UserType";
import { roundToTwo } from "utils/math";
import { ArticlesInput } from "./ArticlesInput";
import { ArticleSyncInput } from "./ArticleSyncInput/ArticleSyncInput";
import DeviationHelper from "./DeviationHelper";

export const Form1 = ({ isEdit, terminalRecord, ...props }) => (
	<FormSpy
		subscription={{ values: true }}
		render={({ values, form }) => {
			if (!isEdit) {
				form.change("duty_manager", terminalRecord.duty_manager_id);
			}
			return (
				<GridForm {...props}>
					<GridInput
						sm={6}
						component={SelectEnumInput}
						source="deviation_main_category"
						enumName="deviation_main_category"
						disabled={isEdit}
						enumObj={DeviationMainCategory}
						sortable={false}
						validate={[required()]}
					/>
					<GridInput
						sm={6}
						component={SelectEnumInput}
						source="deviation_type"
						enumName="deviation_type"
						disabled={isEdit || values.deviation_main_category === undefined}
						enumObj={DeviationTypeByMainCategory[values.deviation_main_category] || {}}
						sortable={false}
						validate={[required()]}
					/>
					<GridInput
						sm={12}
						status={values.status}
						deviationType={values.deviation_type}
						component={DeviationHelper}
					/>
				</GridForm>
			);
		}}
	/>
);

export const Form2 = ({ orderRecord, ...props }) => (
	<FormSpy
		subscription={{ values: true }}
		render={({ values }) => (
			<GridForm {...props}>
				<GridInput
					sm={12}
					md={6}
					component={TextInput}
					source="description"
					multiline
					rows={6}
					validate={[maxLength(1024)]}
				/>
				<GridInput
					sm={12}
					component={BooleanInput}
					source="is_storage_correction_required"
					label="resources.deviations.fields.is_storage_correction_required"
					defaultValue={false}
				/>
				{values.is_storage_correction_required && (
					<GridInput sm={12} md={6}>
						<PlaceSelectArrayInput
							source="area_ids"
							label="resources.deviations.fields.area_ids"
							terminalId={orderRecord.terminal_id}
							filter={{ is_deleted: false }}
						/>
					</GridInput>
				)}
				<GridInput
					sm={12}
					status={values.status}
					deviationType={values.deviation_type}
					component={DeviationHelper}
				/>
			</GridForm>
		)}
	/>
);

export const Form3 = ({ orderRecord, ...props }) => {
	const { permissions } = usePermissions();
	const isNotCustomer = permissions && isInArray(NotCustomerRoles, permissions.role);

	return (
		<FormSpy
			subscription={{ values: true, form: true }}
			render={({ values, form }) => {
				const newValue = roundToTwo((values.kilos || 0) * (values.price_per_kilo || 0));
				form.change("value", newValue);
				return (
					<GridForm {...props}>
						<GridInput
							sm={12}
							md={6}
							source="kilos"
							component={NumberInputEx}
							type="float"
							digitsCount={2}
						/>
						<GridInput sm={12} md={6} source="pallets" component={NumberInputEx} />
						<GridInput sm={12} md={4} source="boxes" component={NumberInputEx} />
						<GridInput
							sm={12}
							md={4}
							source="price_per_kilo"
							component={NumberInputEx}
							type="float"
							digitsCount={2}
						/>
						<GridInput
							sm={12}
							md={4}
							source="value"
							component={NumberInputEx}
							type="float"
							digitsCount={2}
							disabled
						/>
						{isNotCustomer && (
							<GridInput
								sm={12}
								source="is_articles_required"
								component={BooleanInput}
								label="Add articles?"
								defaultValue={false}
							/>
						)}
						{isNotCustomer && values.is_articles_required && (
							<GridInput sm={12}>
								<ArticlesInput orderRecord={orderRecord} code_patterns={["5***", "60**"]} />
							</GridInput>
						)}
						<GridInput
							sm={12}
							status={values.status}
							deviationType={values.deviation_type}
							component={DeviationHelper}
						/>
					</GridForm>
				);
			}}
		/>
	);
};

export const Form4 = ({ orderRecord, terminalRecord, ...props }) => (
	<FormSpy
		subscription={{ values: true }}
		render={({ values }) => (
			<GridForm {...props}>
				{/* <FormGroupGrid sm={12} md={6}> */}
				<GridInput
					sm={4}
					component={TextInput}
					defaultValue={orderRecord.reference}
					source="reference"
					disabled
				/>
				<GridInput
					sm={4}
					component={CompanyText}
					defaultValue={orderRecord.company_id}
					source="company_id"
					label="Customer"
					disabled
				/>
				<GridInput
					sm={4}
					component={DateTimeInput}
					source="created_at"
					defaultValue={orderRecord.created_at}
					disabled
				/>
				<GridInput
					source="assignee_1"
					defaultValue={orderRecord.assignee_1}
					enableGetChoices={() => false}
					disabled
					sm={6}
					component={OSFCUserSelectInput}
				/>

				{values.deviation_main_category !== DeviationMainCategory.receiving && (
					<GridInput
						source="assignee_2"
						defaultValue={orderRecord.assignee_2}
						enableGetChoices={() => false}
						disabled
						sm={6}
						component={OSFCUserSelectInput}
					/>
				)}
				<GridInput
					source="duty_manager"
					sm={6}
					component={OSFCUserSelectInput}
					enableGetChoices={() => false}
					disabled
					filter={{ type: UserType.manager }}
				/>
				<GridInput
					source="managers_to_notify"
					sm={12}
					component={OSFCUsersSelectInput}
					enableGetChoices={() => {
						return !props.disabled;
					}}
					filter={{ type: UserType.manager }}
				/>
				{/* </FormGroupGrid> */}
				{/* <FormGroupGrid sm={12} md={6}> */}
				{/* </FormGroupGrid> */}
				<GridInput
					sm={12}
					status={values.status}
					deviationType={values.deviation_type}
					component={DeviationHelper}
				/>
			</GridForm>
		)}
	/>
);

export const DocumentsField = ({ isEdit, disabled, ...props }) => {
	const translate = useTranslate();
	const documentTypeChoices = useEnumSelectChoices(
		DeviationDocumentType,
		"deviation_document_type",
		true
	);
	disabled = !isEdit || disabled;
	const validateDocuments = React.useCallback(
		(value, allValues) => {
			if (
				!disabled ||
				!IsImageRequiredByDeviationType[allValues.deviation_type] ||
				!allValues.action
			)
				return undefined;
			const error = translate("resources.deviations.validation.images_required");
			if (!Array.isArray(value) || value.length === 0) {
				return error;
			}
			const hasImage = value.some((item) => item && item.type === DeviationDocumentType.IMAGES);
			if (!hasImage) return error;
			return undefined;
		},
		[translate]
	);

	const getFooterContent = (showError, error) => {
		let errorText;
		if (disabled)
			if (isEdit) {
				errorText = translate("resources.deviations.document_field_disabled_for_the_status");
			} else {
				errorText = translate("resources.deviations.document_add_warning");
			}
		else if (showError) errorText = error;

		if (errorText)
			return (
				<Typography align="center" variant="body2" color="error">
					{errorText}
				</Typography>
			);
	};

	return (
		<Field
			name="documents_list"
			validate={validateDocuments}
			render={({ input, meta }) => {
				const showError = meta.touched && !!meta.error;
				return (
					<FormControl fullWidth margin="normal" error={showError}>
						<DocumentsFieldBlock
							isTypeShow={!disabled}
							isTypeRequired={true}
							hideEditDialog={true}
							typeChoices={documentTypeChoices}
							disabled={disabled}
							onDataChange={(data) => {
								if (input.value) input.onBlur();
								input.onChange(data);
							}}
							footerContent={getFooterContent(showError, meta.error)}
							{...props}
						/>
					</FormControl>
				);
			}}
		/>
	);
};

export const Form5 = ({ status, ...props }) => {
	const actions = useEnumSelectChoices(EmployeeAction, "deviation_action");

	return (
		<FormSpy
			subscription={{ values: true }}
			render={({ values }) => (
				<GridForm {...props}>
					<GridInput sm={12}>
						<DocumentsField {...props} />
					</GridInput>
					{(status === DeviationStatus.OPEN || status === undefined) && (
						<GridInput sm={12}>
							<ActionToggleButtonGroupInput source="action" choices={actions} />
						</GridInput>
					)}
					<GridInput
						sm={12}
						status={values.status}
						deviationType={values.deviation_type}
						component={DeviationHelper}
					/>
				</GridForm>
			)}
		/>
	);
};

export const FormFirstAtManager = ({ status, orderRecord, ...props }) => {
	const actions = useEnumSelectChoices(FirstManagerAction, "deviation_action");
	const isOrderClosed =
		orderRecord.status === OrderStatus.ORDER_COMPLETED ||
		orderRecord.status === OrderStatus.DELETED;

	return (
		<FormSpy
			subscription={{ values: true }}
			render={({ values }) => (
				<GridForm {...props}>
					{!props.disabled && (
						<GridInput sm={12}>
							<ActionToggleButtonGroupInput source="action" choices={actions} />
						</GridInput>
					)}
					{values.action === FirstManagerAction.close_case && !isOrderClosed && !props.disabled && (
						<GridInput sm={12}>
							<ArticleSyncInput source="articles" label="" orderRecord={orderRecord} />
						</GridInput>
					)}
					<GridInput
						sm={12}
						status={values.status}
						deviationType={values.deviation_type}
						component={DeviationHelper}
					/>
				</GridForm>
			)}
		/>
	);
};

export const FormAtCustomer = ({ status, orderRecord, ...props }) => {
	const customerActions = useEnumSelectChoices(CustomerAction, "deviation_action");
	return (
		<FormSpy
			subscription={{ values: true }}
			render={({ values }) => (
				<GridForm {...props}>
					<GridInput
						sm={12}
						md={6}
						component={TextInput}
						source="customer_notes"
						multiline
						rows={6}
						disabled={props.disabled}
						validate={[maxLength(1024), required()]}
					/>
					{!props.disabled && (
						<GridInput sm={12}>
							<ActionToggleButtonGroupInput source="action" choices={customerActions} />
						</GridInput>
					)}
					<GridInput
						sm={12}
						status={values.status}
						deviationType={values.deviation_type}
						component={DeviationHelper}
					/>
				</GridForm>
			)}
		/>
	);
};

export const FormAtManager = ({ status, orderRecord, ...props }) => {
	const { permissions } = usePermissions();
	const managerActions = useEnumSelectChoices(SecondManagerAction, "deviation_action");
	const isOrderClosed =
		orderRecord.status === OrderStatus.ORDER_COMPLETED ||
		orderRecord.status === OrderStatus.DELETED;

	const isNotCustomer = permissions && isInArray(NotCustomerRoles, permissions.role);

	return (
		<FormSpy
			subscription={{ values: true, form: true }}
			render={({ values, form }) => (
				<GridForm {...props}>
					<GridInput
						sm={12}
						md={6}
						component={TextInput}
						source="customer_notes"
						multiline
						rows={6}
						disabled={true}
						validate={[maxLength(1024), required()]}
					/>
					<FormGroupGrid>
						<GridInput
							sm={12}
							component={BooleanInput}
							source="needs_destruction"
							label="resources.deviations.fields.needs_destruction"
							defaultValue={false}
						/>
						{isNotCustomer && (
							<GridInput
								sm={12}
								source="is_articles_required"
								component={BooleanInput}
								label="Add articles?"
								defaultValue={false}
							/>
						)}
						{isNotCustomer && values.is_articles_required && (
							<GridInput sm={12}>
								<ArticlesInput orderRecord={orderRecord} code_patterns={["5***", "60**", "8095"]} />
							</GridInput>
						)}

						<GridInput
							sm={12}
							component={BooleanInput}
							source="is_storage_correction_required"
							label="resources.deviations.fields.is_storage_correction_required"
							defaultValue={false}
						/>
						{values.is_storage_correction_required && (
							<GridInput sm={12} md={12}>
								<PlaceSelectArrayInput
									source="area_ids"
									label="resources.deviations.fields.area_ids"
									terminalId={orderRecord.terminal_id}
									filter={{ disabled: false }}
								/>
							</GridInput>
						)}
						{!props.disabled && isNotCustomer && status === DeviationStatus.AT_MANAGER && (
							<GridInput sm={12}>
								<ActionToggleButtonGroupInput source="action" choices={managerActions} />
							</GridInput>
						)}
						{!props.disabled &&
							isNotCustomer &&
							status === DeviationStatus.AT_MANAGER &&
							isInArray(
								[
									SecondManagerAction.close_case,
									SecondManagerAction.could_claim,
									SecondManagerAction.could_loss,
									SecondManagerAction.could_negligence,
								],
								values.action
							) &&
							!isOrderClosed && (
								<GridInput sm={12}>
									<ArticleSyncInput source="articles" label="" orderRecord={orderRecord} />
								</GridInput>
							)}
					</FormGroupGrid>

					<GridInput
						sm={12}
						status={values.status}
						deviationType={values.deviation_type}
						component={DeviationHelper}
					/>
				</GridForm>
			)}
		/>
	);
};

export const FormAtManagement = ({ status, orderRecord, ...props }) => {
	const { permissions } = usePermissions();
	const actions = useEnumSelectChoices(ManagementAction, "deviation_action");
	const isOrderClosed =
		orderRecord.status === OrderStatus.ORDER_COMPLETED ||
		orderRecord.status === OrderStatus.DELETED;

	const isNotCustomer = permissions && isInArray(NotCustomerRoles, permissions.role);

	return (
		<FormSpy
			subscription={{ values: true }}
			render={({ values }) => (
				<GridForm {...props}>
					<GridInput
						sm={12}
						md={4}
						component={YesNoINoneInput}
						source="is_claim"
						defaultValue={false}
					/>
					<GridInput
						sm={12}
						md={4}
						source="claim_value"
						component={NumberInputEx}
						type="float"
						digitsCount={2}
					/>
					<GridInput
						sm={12}
						md={4}
						component={TextInput}
						source="claim_invoice"
						validate={[maxLength(16), required()]}
					/>
					{!props.disabled && isNotCustomer && status === DeviationStatus.AT_MANAGEMENT && (
						<GridInput sm={12}>
							<ActionToggleButtonGroupInput source="action" choices={actions} />
						</GridInput>
					)}
					{!props.disabled &&
						isNotCustomer &&
						status === DeviationStatus.AT_MANAGEMENT &&
						Number.isInteger(values.action) &&
						!isOrderClosed && (
							<GridInput sm={12}>
								<ArticleSyncInput source="articles" label="" orderRecord={orderRecord} />
							</GridInput>
						)}
					<GridInput
						sm={12}
						status={values.status}
						deviationType={values.deviation_type}
						component={DeviationHelper}
					/>
				</GridForm>
			)}
		/>
	);
};
